import React from 'react';
import { Button } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { FH131 } from '../../../services/pwh203Api';

interface Props {
  details: FH131[];
  isEditing: boolean;
  onAdd: () => void;
  onDelete: (index: number) => void;
  onUpdate: <K extends keyof FH131>(index: number, key: K, value: FH131[K]) => void;
}

const PAYMENT_METHOD_OPTIONS = [
  { value: '1', label: '現金' },
  { value: '2', label: '押匯' },
  { value: '3', label: '支票' },
  { value: '4', label: '電匯' },
  { value: '5', label: '轉帳' },
  { value: '6', label: '沖暫借款' },
  { value: '7', label: '沖預收款' },
  { value: '8', label: '應付代付款' },
];

const Tab2Details: React.FC<Props> = React.memo(({ details, isEditing, onAdd, onDelete, onUpdate }) => {
  const totalRequest = details.reduce((sum, d) => sum + (d.fh131_17 || 0), 0);
  const totalActual = details.reduce((sum, d) => sum + (d.fh131_22 || 0), 0);

  return (
    <div className="pwh203-tab-content">
      {isEditing && (
        <div className="tab-toolbar">
          <Button icon={<PlusOutlined />} size="small" onClick={onAdd}>
            新增明細
          </Button>
        </div>
      )}

      <div className="pwh203-table-wrapper">
        <table className="pwh203-table">
          <thead>
            <tr>
              <th style={{ width: 45 }}>項次</th>
              <th style={{ width: 80 }}>請款單號</th>
              <th style={{ width: 100 }}>付款方式</th>
              <th style={{ width: 120 }}>銀行帳號</th>
              <th style={{ width: 100 }}>票據號碼</th>
              <th style={{ width: 110 }}>請款金額</th>
              <th style={{ width: 110 }}>實付金額</th>
              <th style={{ width: 80 }}>幣別</th>
              <th style={{ width: 80 }}>銀行代號</th>
              <th style={{ width: 100 }}>受款人</th>
              <th style={{ width: 110 }}>支票到期日</th>
              {isEditing && <th style={{ width: 50 }}>刪除</th>}
            </tr>
          </thead>
          <tbody>
            {details.map((row, idx) => (
              <tr key={idx}>
                <td className="text-center">{row.fh131_02}</td>
                <td>
                  <input
                    className="cell-input"
                    value={row.fh131_11}
                    onChange={(e) => onUpdate(idx, 'fh131_11', e.target.value)}
                    disabled={!isEditing}
                    maxLength={6}
                  />
                </td>
                <td>
                  <select
                    className="cell-input"
                    value={row.fh131_12}
                    onChange={(e) => onUpdate(idx, 'fh131_12', e.target.value)}
                    disabled={!isEditing}
                  >
                    <option value="">—</option>
                    {PAYMENT_METHOD_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    className="cell-input"
                    value={row.fh131_13}
                    onChange={(e) => onUpdate(idx, 'fh131_13', e.target.value)}
                    disabled={!isEditing}
                    maxLength={20}
                  />
                </td>
                <td>
                  <input
                    className="cell-input"
                    value={row.fh131_14}
                    onChange={(e) => onUpdate(idx, 'fh131_14', e.target.value)}
                    disabled={!isEditing}
                    maxLength={10}
                  />
                </td>
                <td>
                  <input
                    className="cell-input text-right"
                    type="number"
                    step="0.01"
                    value={row.fh131_17}
                    onChange={(e) => onUpdate(idx, 'fh131_17', parseFloat(e.target.value) || 0)}
                    disabled={!isEditing}
                  />
                </td>
                <td>
                  <input
                    className="cell-input text-right"
                    type="number"
                    step="0.01"
                    value={row.fh131_22}
                    onChange={(e) => onUpdate(idx, 'fh131_22', parseFloat(e.target.value) || 0)}
                    disabled={!isEditing}
                  />
                </td>
                <td>
                  <input
                    className="cell-input"
                    value={row.fh131_16}
                    onChange={(e) => onUpdate(idx, 'fh131_16', e.target.value.toUpperCase())}
                    disabled={!isEditing}
                    maxLength={3}
                  />
                </td>
                <td>
                  <input
                    className="cell-input"
                    value={row.fh131_21}
                    onChange={(e) => onUpdate(idx, 'fh131_21', e.target.value)}
                    disabled={!isEditing}
                    maxLength={6}
                  />
                </td>
                <td>
                  <input
                    className="cell-input"
                    value={row.fh131_23}
                    onChange={(e) => onUpdate(idx, 'fh131_23', e.target.value)}
                    disabled={!isEditing}
                    maxLength={20}
                  />
                </td>
                <td>
                  <input
                    className="cell-input"
                    type="date"
                    value={row.fh131_15 ? String(row.fh131_15).substring(0, 10) : ''}
                    onChange={(e) => onUpdate(idx, 'fh131_15', e.target.value ? e.target.value + 'T00:00:00Z' : null)}
                    disabled={!isEditing}
                  />
                </td>
                {isEditing && (
                  <td className="text-center">
                    <Button
                      icon={<DeleteOutlined />}
                      size="small"
                      danger
                      onClick={() => onDelete(idx)}
                    />
                  </td>
                )}
              </tr>
            ))}
            {details.length === 0 && (
              <tr>
                <td colSpan={isEditing ? 12 : 11} className="text-center no-data">
                  尚無明細資料
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="total-row">
              <td colSpan={5} className="text-right"><strong>合計</strong></td>
              <td className="text-right">
                <strong>{totalRequest.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
              </td>
              <td className="text-right">
                <strong>{totalActual.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
              </td>
              <td colSpan={isEditing ? 5 : 4} />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
});

export default Tab2Details;
